/**
 * Draggable column divider, ported from `SplitHandle` in Components.swift.
 *
 * Visual spec mirrors the Mac exactly:
 *   - a 1pt seam line that thickens to 2pt while hovered or dragged,
 *   - a 12pt hit area straddling the boundary so it is easy to grab,
 *   - `col-resize` cursor, with the line colours transitioning smoothly.
 *
 * Two colour schemes, like the Mac call sites: `sidebar` (panda-green
 * accent gradient over `--sidebar-border`) and `content` (neutral border
 * that highlights white, the editor|preview default). Keyboard users can
 * adjust with arrows (8px), Home/End for the range ends.
 */
import { useCallback, useRef, useState } from 'react';

export interface SplitHandleProps {
  /** Controlled current width of the pane left of the divider, px. */
  width: number;
  min: number;
  max: number;
  /** Called continuously while dragging with the clamped width. */
  onResize: (width: number) => void;
  /** Called once when a drag/keyboard adjustment ends, with the final width. */
  onCommit: (width: number) => void;
  variant: 'sidebar' | 'content';
  /** Accessible name, e.g. "Resize sidebar". */
  label: string;
}

const KEYBOARD_STEP = 8;

export function SplitHandle({
  width,
  min,
  max,
  onResize,
  onCommit,
  variant,
  label,
}: SplitHandleProps) {
  const [dragging, setDragging] = useState(false);
  // Latest width for the pointer-up commit; refs avoid stale closures.
  const widthRef = useRef(width);
  widthRef.current = width;

  const clamp = useCallback(
    (value: number): number => Math.min(Math.max(value, min), max),
    [min, max],
  );

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // Button 0 only; text selection is suppressed via body.col-resizing.
      if (event.button !== 0) return;
      event.preventDefault();
      // Capture the node now: React nulls `event.currentTarget` once this
      // handler returns, so the pointer-up closure must not read it again —
      // a stale read throws inside onUp and leaks the drag listeners.
      const el = event.currentTarget;
      el.setPointerCapture(event.pointerId);
      setDragging(true);
      document.body.classList.add('col-resizing');
      const startX = event.clientX;
      const startWidth = widthRef.current;

      const onMove = (move: PointerEvent): void => {
        onResize(clamp(startWidth + (move.clientX - startX)));
      };
      const onUp = (): void => {
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerup', onUp);
        el.removeEventListener('pointercancel', onUp);
        document.body.classList.remove('col-resizing');
        setDragging(false);
        onCommit(widthRef.current);
      };
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerup', onUp);
      el.addEventListener('pointercancel', onUp);
    },
    [clamp, onCommit, onResize],
  );

  /** Arrow keys nudge the split; Home/End jump to the range ends. */
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      let next: number | null = null;
      if (event.key === 'ArrowLeft') next = widthRef.current - KEYBOARD_STEP;
      else if (event.key === 'ArrowRight') next = widthRef.current + KEYBOARD_STEP;
      else if (event.key === 'Home') next = min;
      else if (event.key === 'End') next = max;
      if (next === null) return;
      event.preventDefault();
      onResize(clamp(next));
      onCommit(clamp(next));
    },
    [clamp, max, min, onCommit, onResize],
  );

  return (
    <div
      className={`split-handle split-${variant}${dragging ? ' is-dragging' : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(width)}
      aria-valuemin={Math.round(min)}
      aria-valuemax={Math.round(max)}
      tabIndex={0}
      title={label}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    />
  );
}
